import { ClickAwayListener } from "@mui/base";
import type { PopperPlacementType } from "@mui/base/Popper";
import {
  ForwardedRef,
  forwardRef,
  JSXElementConstructor,
  KeyboardEvent,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import { Tooltip } from "../../tooltip/tooltip";
import {
  RedoButton,
  RedoButtonSize,
  RedoButtonTheme,
} from "../buttons/redo-button";
import * as redoCommandMenuCss from "./redo-command-menu.module.css";

export interface RedoCommandMenuItem {
  Icon?: JSXElementConstructor<any>;
  text: string;
  onClick(event: MouseEvent<HTMLButtonElement>): void;
  dataTestId?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  tooltip?: string;
  theme?: RedoButtonTheme;
  size?: RedoButtonSize;
  show?: boolean;
  id?: string;
}

export const RedoCommandMenu = memo(function RedoCommandMenu({
  dontRestrainIconWidth,
  items,
  open,
  setOpen,
  anchor,
  size = "sm",
  fitToAnchor = false,
  placement,
  dropdownClassName,
}: {
  dontRestrainIconWidth?: boolean;
  items: RedoCommandMenuItem[];
  open: boolean;
  setOpen(value: boolean): void;
  anchor: HTMLElement | null;
  size?: RedoButtonSize;
  fitToAnchor?: boolean;
  placement?: PopperPlacementType;
  dropdownClassName?: string;
}) {
  const atLeastOneIcon = items.some((item) => item.Icon !== undefined);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const itemsToShow = useMemo(
    () => items.filter((item) => item.show !== false),
    [items],
  );

  // Clean up stale refs when items change
  buttonRefs.current.length = itemsToShow.length;

  // Focus first non-disabled item when menu opens
  useEffect(() => {
    if (open) {
      // Delay needed because dropdown renders via portal
      requestAnimationFrame(() => {
        const firstEnabledIndex = itemsToShow.findIndex(
          (item) => !item.disabled,
        );
        if (firstEnabledIndex >= 0) {
          setFocusedIndex(firstEnabledIndex);
          buttonRefs.current[firstEnabledIndex]?.focus();
        }
      });
    }
  }, [open, itemsToShow]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const enabledIndices = itemsToShow
        .map((item, idx) => (!item.disabled ? idx : -1))
        .filter((idx) => idx !== -1);

      if (enabledIndices.length === 0) return;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const currentPos = enabledIndices.indexOf(focusedIndex);
          // If current position not found, start at first item
          const nextPos =
            currentPos === -1 ? 0 : (currentPos + 1) % enabledIndices.length;
          const nextIndex = enabledIndices[nextPos];
          setFocusedIndex(nextIndex);
          buttonRefs.current[nextIndex]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const currentPos = enabledIndices.indexOf(focusedIndex);
          // If current position not found, start at first item
          const prevPos =
            currentPos === -1
              ? 0
              : (currentPos - 1 + enabledIndices.length) %
                enabledIndices.length;
          const prevIndex = enabledIndices[prevPos];
          setFocusedIndex(prevIndex);
          buttonRefs.current[prevIndex]?.focus();
          break;
        }
        case "Escape": {
          e.preventDefault();
          setOpen(false);
          anchor?.focus();
          break;
        }
      }
    },
    [itemsToShow, focusedIndex, setOpen, anchor],
  );

  const dropdown = (
    <Dropdown
      anchor={anchor}
      className={dropdownClassName}
      fitToAnchor={fitToAnchor}
      open={open}
      placement={placement}
    >
      <div onKeyDown={handleKeyDown} role="menu">
        <Flex dir="column">
          {itemsToShow.map((item, idx) => {
            return (
              <RedoCommandMenuItemComponent
                dontRestrainIconWidth={dontRestrainIconWidth}
                fitToAnchor={fitToAnchor}
                indentForIcon={atLeastOneIcon}
                item={item}
                itemClicked={() => setOpen(false)}
                key={item.id ?? idx}
                ref={(el) => {
                  buttonRefs.current[idx] = el;
                }}
                size={size}
              />
            );
          })}
        </Flex>
      </div>
    </Dropdown>
  );

  return (
    <>
      {open && (
        <ClickAwayListener
          onClickAway={() => {
            setOpen(false);
          }}
        >
          {dropdown}
        </ClickAwayListener>
      )}
    </>
  );
});

/**
 * @param indentForIcon if at least one of the menu items has an icon,
 * we want to space all the buttons evenly
 */
const RedoCommandMenuItemComponent = memo(
  forwardRef(function RedoCommandMenuItemComponent(
    {
      dontRestrainIconWidth,
      item,
      indentForIcon,
      itemClicked,
      size = "sm",
      fitToAnchor = false,
    }: {
      dontRestrainIconWidth?: boolean;
      item: RedoCommandMenuItem;
      indentForIcon: boolean;
      itemClicked(): void;
      size?: RedoButtonSize;
      fitToAnchor?: boolean;
    },
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    const icon = item.Icon || (indentForIcon ? "placeholder" : undefined);

    const button = (
      <RedoButton
        buttonId={item.id}
        centerItems={false}
        className={
          fitToAnchor
            ? redoCommandMenuCss.fullWidthDropdownButton
            : redoCommandMenuCss.dropdownButton
        }
        dataTestId={item.dataTestId}
        disabled={item.disabled}
        dontRestrainIconWidth={dontRestrainIconWidth}
        hierarchy="tertiary"
        IconLeading={icon}
        onClick={(event) => {
          event.stopPropagation();
          itemClicked();
          item.onClick(event);
        }}
        ref={ref}
        size={item.size ?? size}
        text={item.text}
        theme={item.theme}
        useOverflowTooltip
      />
    );
    const tooltipText = (item.disabled && item.disabledTooltip) || item.tooltip;

    if (tooltipText) {
      return (
        <Tooltip arrow title={tooltipText}>
          <div>{button}</div>
        </Tooltip>
      );
    }

    return button;
  }),
);
